/// <amd-module name="Repos/PullRequestMentionAutocomplete/PullRequestMentionDataProvider" />
import { ISuggestionSearchResult as ISearchResult } from "Mention/Autocomplete/Contracts";
import { GitPullRequest, GitPullRequestSearchCriteria, PullRequestStatus, PullRequestTimeRangeType } from "Repos/Common/Contracts/Git";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { caseInsensitiveContains } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { PullRequestMention } from "./Contracts";
import { compareMentions, union } from "./PullRequestAutocompleteUtility";
import { getGitClient } from "./RestClient/Git";

type IDictionaryStringTo<T> = { [key: string]: T };

export interface IPullRequestMentionDataProviderService extends IVssService {
    /**
     * Return a pull request for the given pull request id, hitting the server if necessary
     */
    getPullRequest(id: number): Promise<PullRequestMention | null>;

    /**
     * search the currently cached list of pull requests for any whose title matches the provided search term
     */
    searchPullRequests(searchTerm: string): Promise<ISearchResult<PullRequestMention>[]>;
}

/**
 * This data provider is responsible for fetching the initial list of pull request mention suggestions
 * as well as searching them by title search term or pulling more pr data from the server if
 * requesting a specific pull request id that we don't currently have cached
 */
class PullRequestMentionDataProviderService extends VssService implements IPullRequestMentionDataProviderService {
    private myPullRequests: Promise<PullRequestMention[]>;
    private dataCache: IDictionaryStringTo<Promise<PullRequestMention | null> | undefined> = {};

    /**
     * Upon first initialization (this happens the first time you try to leave a comment)
     * We query for the last 20 pull requests that you authored or were a reviewer on for any pull request status
     * This starts our initial cache of pull requests for searching though if a pull request is searched for by id,
     * then that pull request will get added to the list
     */
    private getMyPullRequests(): Promise<PullRequestMention[]> {
        if (!this.myPullRequests) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();

            if (!tfsPageData || !tfsPageData.project) {
                // if we don't have a project, we can't fetch pull request suggestions for the current project
                this.myPullRequests = Promise.resolve([]);
            } else {
                const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
                const pageData = pageService.getData();
                const userId = (pageData && pageData.user && pageData.user.id) || "";

                const projectId = tfsPageData.project.id;

                const createdByMyCriteria: GitPullRequestSearchCriteria = {
                    repositoryId: "",
                    includeLinks: false,
                    status: PullRequestStatus.All,
                    creatorId: userId,
                    reviewerId: "",
                    sourceRefName: "",
                    targetRefName: "",
                    sourceRepositoryId: "",
                    maxTime: null as unknown as Date,
                    minTime: null as unknown as Date,
                    queryTimeRangeType: null as unknown as PullRequestTimeRangeType

                };
                const assignedToMeCriteria: GitPullRequestSearchCriteria = {
                    repositoryId: "",
                    includeLinks: false,
                    status: PullRequestStatus.All,
                    creatorId: "",
                    reviewerId: userId,
                    sourceRefName: "",
                    targetRefName: "",
                    sourceRepositoryId: "",
                    maxTime: null as unknown as Date,
                    minTime: null as unknown as Date,
                    queryTimeRangeType: null as unknown as PullRequestTimeRangeType
                };

                const gitRestClient = getGitClient(this.pageContext);

                let myPullRequests: PullRequestMention[] = [];

                const createdByMePromise = gitRestClient.getPullRequestsByProject(projectId, createdByMyCriteria, 0, 0, 20).then(prs => {
                    const myCreatedPRs = prs.map(pr => {
                        return {
                            title: pr.title,
                            id: pr.pullRequestId,
                            url: this.getPullRequestUrl(pr)
                        };
                    });

                    myPullRequests = union(myPullRequests, myCreatedPRs, (a, b) => a && b && a.id - b.id);
                });

                const assignedToMePromise = gitRestClient.getPullRequestsByProject(projectId, assignedToMeCriteria, 0, 0, 10).then(prs => {
                    const myAssignedPRs = prs.map(pr => {
                        return {
                            title: pr.title,
                            id: pr.pullRequestId,
                            url: this.getPullRequestUrl(pr)
                        };
                    });

                    myPullRequests = union(myPullRequests, myAssignedPRs, (a, b) => a && b && a.id - b.id);
                });

                return Promise.all([createdByMePromise, assignedToMePromise]).then(() => {
                    return myPullRequests;
                }).catch((error: unknown) => {
                    console.error(error);
                    return myPullRequests;
                });
            }
        }

        return this.myPullRequests;
    }

    /**
     * Return a pull request for the given pull request id, hitting the server if necessary
     */
    public getPullRequest(id: number): Promise<PullRequestMention | null> {
        let promise: Promise<PullRequestMention | null>;

        if (this.dataCache[id.toString()]) {
            promise = this.dataCache[id.toString()] as Promise<PullRequestMention>;
        } else {
            if (id > 0) {
                promise = getGitClient(this.pageContext)
                    .getPullRequestById(id)
                    .then(
                        pr => {
                            return {
                                title: pr.title,
                                id: pr.pullRequestId,
                                url: this.getPullRequestUrl(pr)
                            } as PullRequestMention;
                        },
                        error => {
                            return null;
                        }
                    );
            } else {
                promise = Promise.resolve(null);
            }

            this.dataCache[id.toString()] = promise;
        }

        return promise;
    }

    /**
     * search the currently cached list of pull requests for any whose title matches the provided search term
     */
    public searchPullRequests(searchTerm: string): Promise<ISearchResult<PullRequestMention>[]> {
        const mentionPromises = Object.keys(this.dataCache).map(k => this.dataCache[k]);
        return Promise.all(mentionPromises)
            .then(responses => {
                const foundPRs: ISearchResult<PullRequestMention>[] = [];
                responses.forEach(prMention => {
                    if (prMention && this.shouldIncludePR(prMention, searchTerm)) {
                        foundPRs.push({ original: prMention, highlighted: prMention });
                    }
                });
                return foundPRs;
            })
            .then(foundPrs => {
                const foundMyPrs: ISearchResult<PullRequestMention>[] = [];
                return this.getMyPullRequests().then(myPrs => {
                    myPrs.forEach(pr => {
                        if (this.shouldIncludePR(pr, searchTerm)) {
                            foundMyPrs.push({ original: pr, highlighted: pr });
                        }
                    });

                    return union(foundPrs, foundMyPrs, compareMentions);
                });
            });
    }

    private shouldIncludePR(prMention: PullRequestMention, searchTerm: string): boolean {
        return (
            searchTerm === "" || caseInsensitiveContains(prMention.title, searchTerm) || caseInsensitiveContains(prMention.id.toString(), searchTerm)
        );
    }

    private getPullRequestUrl(pr: GitPullRequest): string {
        const navigationService = this.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return navigationService.getPullRequestUrl(pr.repository.name, pr.repository.project.name, pr.pullRequestId);
    }
}

Services.add("IPullRequestMentionDataProviderService", { serviceFactory: PullRequestMentionDataProviderService });

export function getPullRequestMentionProviderService(pageContext: IVssPageContext): IPullRequestMentionDataProviderService {
    return pageContext.getService<IPullRequestMentionDataProviderService>("IPullRequestMentionDataProviderService");
}
